import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { solutionKeys } from "@/lib/solution-keys";

export type MediaType = "audio" | "video" | "mind_map" | "document" | "other";

export interface MediaLinkRow {
  id: string;
  solutionId: string;
  label: string;
  url: string;
  type: MediaType;
  showOnShare: boolean;
  sortOrder: number;
  createdBy: string;
  createdAt: string;
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) {
    let message = `${res.status}`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export function useMedia(slug: string) {
  return useQuery({
    queryKey: solutionKeys.media(slug),
    enabled: Boolean(slug),
    queryFn: async (): Promise<MediaLinkRow[]> => {
      const json = await api<{ data: MediaLinkRow[] }>(`/api/solutions/${slug}/media`);
      return json.data;
    },
  });
}

export function useCreateMedia(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      label: string;
      url: string;
      type: MediaType;
      showOnShare?: boolean;
    }) => {
      const json = await api<{ data: MediaLinkRow }>(`/api/solutions/${slug}/media`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: solutionKeys.media(slug) });
      toast.success("Media link added");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to add media link");
    },
  });
}

export function useUpdateMedia(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      mediaId: string;
      label?: string;
      url?: string;
      type?: MediaType;
      showOnShare?: boolean;
    }) => {
      const { mediaId, ...rest } = input;
      const json = await api<{ data: MediaLinkRow }>(`/api/media/${mediaId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(rest),
      });
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: solutionKeys.media(slug) });
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to update media link");
    },
  });
}

export function useDeleteMedia(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mediaId: string) => {
      await api(`/api/media/${mediaId}`, { method: "DELETE" });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: solutionKeys.media(slug) });
      toast.success("Media link removed");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to remove media link");
    },
  });
}
